import axios from "axios";
import { pasangDeteksiMaintenance } from "@/utils/maintenance";

const api = axios.create({
  baseURL: process.env.VUE_APP_GATEWAY,
  headers: {
    "Content-Type": "application/json",
  },
});

// 503 maintenance dari BE → layar pemeliharaan (lihat utils/maintenance.js)
pasangDeteksiMaintenance(api);

// ✅ Interceptor untuk menambahkan Authorization token ke setiap request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 🔹 Status proses refresh token (single-flight) + antrian request yang menunggu
let isRefreshing = false;
let refreshSubscribers = [];
let loggingOut = false;

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Bereskan SEMUA request yang menunggu hasil refresh — baik sukses MAUPUN gagal —
// supaya tidak ada request yang menggantung selamanya. result = { token, expired }.
const flushSubscribers = (result) => {
  const subscribers = refreshSubscribers;
  refreshSubscribers = [];
  subscribers.forEach((callback) => callback(result));
};

// Logout dijalankan sekali saja (idempotent) dan mengarah ke halaman login.
const forceLogout = () => {
  if (loggingOut) return;
  loggingOut = true;
  localStorage.clear();
  window.location.href = `${process.env.VUE_APP_URL}login`;
};

// ✅ Refresh token (single-flight). Mengembalikan { token, expired }:
//    - token   : string token baru bila sukses, null bila gagal.
//    - expired : true HANYA bila server eksplisit menolak refresh token (400/401)
//                → sesi benar-benar habis, harus login ulang. Untuk kegagalan
//                transien (jaringan/timeout/5xx) expired=false → sesi DIPERTAHANKAN.
const refreshTokenRequest = async () => {
  if (isRefreshing) {
    // Sudah ada refresh berjalan → ikut antri, JANGAN memicu refresh kedua (yang
    // akan membawa token lama yang sudah di-blacklist server → 401 → logout liar).
    return new Promise((resolve) => {
      subscribeTokenRefresh((result) => resolve(result));
    });
  }

  isRefreshing = true;
  let result = { token: null, expired: false };

  try {
    const oldToken = localStorage.getItem("token");
    if (!oldToken) {
      // Tidak ada token sama sekali → memang harus login.
      result = { token: null, expired: true };
    } else {
      const response = await axios.post(
        process.env.VUE_APP_GATEWAY + "refresh-token",
        {},
        {
          headers: {
            Authorization: `Bearer ${oldToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.status === 200 && response.data.access_token) {
        localStorage.setItem("token", response.data.access_token);
        result = { token: response.data.access_token, expired: false };
      } else {
        // Server membalas tanpa token baru yang sah → anggap sesi habis.
        result = { token: null, expired: true };
        console.error("❌ Refresh token gagal: respons tanpa access_token");
      }
    }
  } catch (error) {
    const status = error.response?.status;
    // Sesi dianggap habis HANYA bila server menolak token refresh (400/401).
    // Network error / timeout / 5xx = kegagalan sementara → JANGAN logout.
    result = { token: null, expired: status === 400 || status === 401 };
    console.error("❌ Gagal refresh token:", error.response?.data || error.message);
  } finally {
    isRefreshing = false;
    flushSubscribers(result);
  }

  return result;
};

// ✅ Interceptor respons: auto-refresh pada 401, TANPA logout berlebihan.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response &&
      error.response.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      const { token, expired } = await refreshTokenRequest();

      if (token) {
        originalRequest.headers["Authorization"] = `Bearer ${token}`;
        return api(originalRequest); // 🔁 ulangi request dengan token baru
      }

      // Tidak mendapat token baru. Logout HANYA bila sesi benar-benar berakhir
      // (server menolak refresh token). Bila hanya gagal transien atau kalah
      // balapan refresh, JANGAN hapus sesi — biarkan request ini gagal; permintaan
      // berikutnya akan memicu refresh ulang dan pulih sendiri.
      if (expired) {
        console.warn("⚠️ Sesi berakhir, mengarahkan ke halaman login");
        forceLogout();
      }
    }

    return Promise.reject(error);
  }
);

export default api;
